import { ChevronLeft, ChevronRight } from "lucide-react";
import { toISODate, monthLabel, shiftMonth, startOfMonth } from "@/lib/format";

const WEEKDAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];

export const DAY_STYLES = {
  available: "border-white/10 bg-white/[0.02] text-neutral-100 hover:border-gold/70 hover:bg-gold/10",
  booked: "border-rose-500/30 bg-rose-500/10 text-rose-300",
  blocked: "border-neutral-700 bg-neutral-800/70 text-neutral-500 cal-hatch",
  partial: "border-amber-400/30 bg-amber-400/10 text-amber-200 hover:border-amber-300/60",
  conflict: "border-white/5 bg-transparent text-neutral-600",
  past: "border-transparent bg-transparent text-neutral-700",
  selected: "border-gold bg-gold text-black font-semibold shadow-[0_0_24px_rgba(200,169,106,0.35)]",
  range: "border-gold/40 bg-gold/15 text-gold-light",
};

const LEGEND = {
  available: "Tersedia",
  booked: "Terisi",
  blocked: "Ditutup",
  partial: "Sebagian terisi",
  selected: "Dipilih",
  conflict: "Durasi bentrok",
};

const DayCell = ({ iso, info, onSelect, testId, cellH }) => (
  <button
    type="button"
    data-testid={`${testId}-day-${iso}`}
    disabled={info.disabled}
    onClick={() => onSelect?.(iso)}
    title={info.note || ""}
    className={`relative ${cellH} rounded-lg border font-mono text-sm transition-[background-color,border-color,transform] duration-200 enabled:active:scale-95 disabled:cursor-not-allowed ${
      DAY_STYLES[info.state] || DAY_STYLES.available
    } ${info.selected ? "ring-2 ring-gold ring-offset-2 ring-offset-black" : ""}`}
  >
    {Number(iso.slice(8))}
    {info.badge && (
      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[9px] leading-none opacity-80">{info.badge}</span>
    )}
  </button>
);

export const MonthCalendar = ({ month, onMonthChange, getDay, onSelect, testId = "calendar", minMonth, size = "md" }) => {
  const year = month.getFullYear();
  const m = month.getMonth();
  const lead = (new Date(year, m, 1).getDay() + 6) % 7;
  const total = new Date(year, m + 1, 0).getDate();
  const days = Array.from({ length: total }, (_, i) => toISODate(new Date(year, m, i + 1)));
  const canPrev = !minMonth || month > startOfMonth(minMonth);
  const cellH = size === "lg" ? "h-12 sm:h-16" : "h-10 sm:h-11";
  const navBtn = "flex h-9 w-9 items-center justify-center rounded-full border border-white/10 transition-colors hover:border-gold/60 hover:text-gold disabled:opacity-30";

  return (
    <div data-testid={testId}>
      <div className="mb-5 flex items-center justify-between">
        <button type="button" className={navBtn} disabled={!canPrev} onClick={() => onMonthChange(shiftMonth(month, -1))} data-testid={`${testId}-prev`}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="font-display text-xl capitalize" data-testid={`${testId}-month-label`}>{monthLabel(month)}</p>
        <button type="button" className={navBtn} onClick={() => onMonthChange(shiftMonth(month, 1))} data-testid={`${testId}-next`}>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="mb-2 grid grid-cols-7 gap-1.5 text-center text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        {WEEKDAYS.map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: lead }, (_, i) => <div key={`b${i}`} />)}
        {days.map((iso) => (
          <DayCell key={iso} iso={iso} info={getDay(iso)} onSelect={onSelect} testId={testId} cellH={cellH} />
        ))}
      </div>
    </div>
  );
};

export const CalendarLegend = ({ items = ["available", "booked", "blocked"] }) => (
  <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-neutral-400" data-testid="calendar-legend">
    {items.map((key) => (
      <span key={key} className="inline-flex items-center gap-2">
        <span className={`h-3.5 w-3.5 rounded border ${DAY_STYLES[key]}`} />
        {LEGEND[key]}
      </span>
    ))}
  </div>
);
